// src/components/Home.js

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

import { CheckCircle, ChevronDown, MapPin, Navigation as NavigationIcon, Clock } from "lucide-react";

import logo from "../imagenes/logo.png";
import img1 from "../imagenes/ins1.webp";
import img2 from "../imagenes/ins2.webp";
import img3 from "../imagenes/ins3.webp";

import "../styles/Home.css";

const heroSlides = [
   {
    image: img1,
    title: (
      <>
        Cuidado Cercano, <br />
        <span className="hero-title-accent">Confianza Absoluta</span>
      </>
    ),
    subtitle: "Creemos en una medicina que acompaña, que escucha y que pone tu bienestar por encima de todo.",
  },
  {
    image: img2,
    title: (
      <>
        Tecnología y Cuidado <br />
        <span className="hero-title-accent">a tu Alcance</span>
      </>
    ),
    subtitle: "Integramos la innovación para ofrecerte un diagnóstico preciso y un seguimiento detallado de tu salud.",
  },
  {
    image: img3,
    title: (
      <>
        Un Equipo <br />
        <span className="hero-title-accent">Comprometido con Usted</span>
      </>
    ),
    subtitle: "Todas las especialidades medicas para brindarte asi el mejor cuidado.",
  },
];

const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut", staggerChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const Home = () => {
  const [activeSlide, setActiveSlide] = useState(0);

  const pilares = [
    { icon: <CheckCircle size={30} />, title: "Cuidado Centrado en la Persona", text: "Cada plan de tratamiento es único, diseñado en torno a las necesidades y deseos del paciente para garantizar su bienestar." },
    { icon: <CheckCircle size={30} />, title: "Compromiso y empatía", text: "Tratar a cada persona con respeto, calidez y dignidad, reconociendo sus necesidades emocionales además de las físicas, y acompañándola durante todo su proceso de atención." },
    { icon: <CheckCircle size={30} />, title: "Enfoque Interdisciplinario", text: "Nuestros especialistas colaboran estrechamente para ofrecer una atención integral, coordinada y completa." },
  ];

  return (
    <main className="home-container">
      <header className="hero-carousel-section">
        <a href="#home" className="main-logo-link">
          <img src={logo} alt="Logo de la Clínica" className="main-logo" />
        </a>
        <motion.a
          href="#ubicacion" 
          className="scroll-down-indicator"
          aria-label="Desplazarse hacia abajo"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 1, ease: "easeInOut" }}
        >
          <ChevronDown size={32} />
        </motion.a>
        <Swiper
          modules={[Navigation, Pagination, Autoplay, EffectFade]}
          onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
          spaceBetween={0}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          loop={true}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          effect="fade"
          fadeEffect={{ crossFade: true }}
          className="hero-carousel"
        >
          {heroSlides.map((slide, index) => (
            <SwiperSlide key={index}>
              <div className="slide-background">
                <img src={slide.image} alt="Fondo de la clínica" className="slide-background-image" />
              </div>
              <div className="slide-overlay" />
              <div className="slide-content">
                <AnimatePresence>
                  {activeSlide === index && (
                    <motion.div
                      className="hero-text-content"
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -30 }}
                      transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                    >
                      <h1 className="hero-title">{slide.title}</h1>
                      <p className="hero-subtitle">{slide.subtitle}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </header>
      
      <motion.section
        id="ubicacion"
        className="location-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={sectionVariants}
      >
        <div className="location-grid section-container">
          <motion.div className="location-content" variants={itemVariants}>
            <div className="location-icon">
              <MapPin size={40} />
            </div>
            <h2 className="location-title">Encontranos en el Corazón de San Rafael</h2>
            <p className="location-address">
              Av. El Libertador 254, M5600 <br />
              San Rafael, Mendoza.
            </p>
            <div className="location-hours">
              <Clock size={18} />
              <span>Guardia 24hs / Administración de 8 a 20hs</span>
            </div>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Av.+El+Libertador+254,+San+Rafael,+Mendoza"
              className="location-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              <NavigationIcon size={20} />
              Cómo llegar
            </a>
          </motion.div>
          <motion.div className="location-map" variants={itemVariants}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3283.921396997018!2d-68.3340889847701!3d-34.60613298045898!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x967907f3a3f31a2d%3A0x2d45c6e3a0b4d4b3!2sAv.%20El%20Libertador%20254%2C%20M5600%20San%20Rafael%2C%20Mendoza!5e0!3m2!1sen!2sar!4v1664223154871!5m2!1sen!2sar"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ubicación de Clínica Ciudad San Rafael"
            ></iframe>
          </motion.div>
        </div>
      </motion.section>

      <motion.section 
        id="pilares" 
        className="pilares-section"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="section-container">
            <motion.h2 className="section-title" variants={itemVariants}>
              Pilares de Nuestra Atención
            </motion.h2>
            <motion.p className="section-subtitle" variants={itemVariants}>
              Creemos en un cuidado que va más allá de la medicina. Nos enfocamos en la dignidad, el respeto y la calidad de vida de cada uno de nuestros pacientes.
            </motion.p>
            <div className="pilares-grid">
              {pilares.map((pilar, index) => (
                <motion.div className="pilar-card" key={index} variants={itemVariants} whileHover={{ y: -10, boxShadow: "var(--shadow-heavy)" }}>
                  <div className="pilar-icon">{pilar.icon}</div>
                  <h3 className="pilar-title">{pilar.title}</h3>
                  <p className="pilar-text">{pilar.text}</p>
                </motion.div>
              ))}
            </div>
        </div>
      </motion.section>
      
    </main>
  );
};

export default Home;